import { css } from '@emotion/react';
import React from 'react';

import { SearchResultRoomCard } from '@/availability/components/results';
import SearchResultRoomCardTitle from '@/availability/components/results/SearchResultRoomCardTitle.component';
import { IRoomAvailabilityInstance } from '@/availability/models';
import { Flex } from '@/box';
import HotelRoomSpecItem from '@/hox-core/components/HotelRoomSpecItem.component';
import HotelRoomSpecs from '@/hox-core/components/HotelRoomSpecs.component';
import { useTranslation } from '@/i18n';
import { Markdown } from '@/markdown';
import { Divider } from '@/ui/layout';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall } from '@/ui/typography';

interface NonBookableRoomCardProps {
  phone?: string;
  email: string;
  name: string;
  description: string;
  extras: IRoomAvailabilityInstance['room']['extras'];
  images: IRoomAvailabilityInstance['room']['images'];
}

export const NonBookableRoomCard = ({
  phone,
  email,
  name,
  description,
  extras,
  images,
}: NonBookableRoomCardProps) => {
  const { t } = useTranslation('search');
  const { keylines, colors, componentProperties } = useTheme();

  return (
    <SearchResultRoomCard testId="non-bookable-room-card" images={images}>
      <Flex flexDirection="column" flex={1}>
        <SearchResultRoomCardTitle name={name} />

        <Spacer s="xs" />

        {extras.length > 0 && (
          <>
            <Divider />
            <HotelRoomSpecs
              css={css({
                padding: '16px 0',
                ...componentProperties.bedroomCard?.roomSpecItemsList,
              })}
            >
              {extras.map((extra) => (
                <HotelRoomSpecItem
                  label={extra.title ?? ''}
                  value={extra.text ?? ''}
                  id={extra.id}
                  key={extra.id + extra.title + extra.text}
                />
              ))}
            </HotelRoomSpecs>
            <Divider />
            <Spacer s="xs" />
          </>
        )}

        <BodySmall>{description}</BodySmall>

        <Spacer s="m" />

        <Flex
          css={{
            flexDirection: 'column',
            padding: '24px 24px 26px',
            borderRadius: '8px',
            border: keylines?.border,
            backgroundColor: colors.background,
            gap: '8px',
          }}
        >
          <BodySmall>{t('roomCard.contact.description')}</BodySmall>

          {phone && (
            <BodySmall>
              <Markdown
                source={t('roomCard.contact.phone', {
                  phoneNumber: phone,
                  phoneNumberEncoded: phone.replace(/[\s()]/g, ''),
                })}
              />
            </BodySmall>
          )}

          <BodySmall>
            <Markdown source={t('roomCard.contact.email', { email })} />
          </BodySmall>
        </Flex>
      </Flex>
    </SearchResultRoomCard>
  );
};
